import React from "react";
import { usePageSettings } from "../../../../contexts/PageSettingsContext";
import { useFetchSingleAddonPrice } from "../../../../hooks/useFetchSingleAddon";
import { BeatLoader } from "react-spinners";

export const ConfigurationsSummary = ({ values, paymentCycle }) => {
	//console.log("VALUES", values);
	const { translations } = usePageSettings();

	// Fetch price for BGP addon
	const { data: bgpPrice, isLoading: bgpPriceLoading } = useFetchSingleAddonPrice({
		addonId: values.configuration.bgp?.id,
		paymentCycle,
	});

	// Fetch price for Additional IP addon
	const { data: additionalIpPrice, isLoading: additionalIpPriceLoading } = useFetchSingleAddonPrice({
		addonId: values.configuration.additionalIp?.id,
		paymentCycle,
	});

	// Fetch price for OS  addon
	const { data: osPrice, isLoading: osPriceLoading } = useFetchSingleAddonPrice({
		addonId: values.configuration.os?.id,
		paymentCycle,
	});

	const hasAnyConfiguration =
		values.configuration?.serverName ||
		values.configuration?.bgp?.id ||
		values.configuration?.os?.id ||
		values.configuration?.additionalIp?.name;

	if (!hasAnyConfiguration) return null;

	return (
		<div className="flex flex-col">
			<h3 className="font-bold text-xs text-ipxon-white">{translations["ConfigurationSummaryTitle"]}</h3>
			<div className="flex justify-between pt-4">
				<div className="flex gap-4 items-center">
					<div className="flex flex-col gap-2">
						<div className="flex justify-between">
							{/* Hostname */}
							{values.configuration?.serverName && (
								<div className="flex justify-start gap-4 text-sm text-ipxon-white">
									<span className="flex-grow flex w-full gap-2 items-center text-sm text-ipxon-medium-gray">
										{translations["ConfigurationSummaryHostname"]}
									</span>
								</div>
							)}

						</div>


						{/* BGP Session */}
						{values.configuration.bgp?.id && (
							<div className="flex justify-start gap-4 text-sm text-ipxon-white">
								<span className="flex-grow flex w-full gap-2 items-center text-sm text-ipxon-medium-gray whitespace-nowrap">
									{translations["ConfigurationSummaryBGPSesion"]}
								</span>
							</div>
						)}

						{/* OS Session */}
						{values.configuration.os?.id && !osPriceLoading && (
							<div className="flex justify-start gap-4 text-sm">
								<span className="text-ipxon-medium-gray">OS:</span>
							</div>
						)}

						{/* Additional IPs */}
						{values.configuration.additionalIp?.name && (
							<div className="flex justify-between text-sm">
								<span className="text-ipxon-medium-gray whitespace-nowrap">
									{translations["ConfigurationSummaryAditionalIP"]}
								</span>
							</div>
						)}
					</div>

					<div className="flex flex-col gap-2">
						{/* Hostname */}
						{values.configuration?.serverName && (
							<div className="relative group max-w-[140px] 2xl:max-w-[170px]">
								<span className="text-ipxon-white text-sm overflow-x-hidden text-ellipsis whitespace-nowrap block">
									{values.configuration.serverName}
								</span>
								<div className="absolute top-full left-0 z-10 hidden group-hover:block bg-neutral-900 text-white text-xs p-2 rounded shadow-md mt-1 whitespace-normal max-w-[300px]">
									{values.configuration.serverName}
								</div>
							</div>
						)}

						{/* BGP Session */}
						{values.configuration.bgp?.id && (
							<div className="flex justify-start gap-2.5 text-sm text-ipxon-white">
								<span className="text-ipxon-white">{values.configuration.bgp?.id ? translations["Sí"] : "No"}</span>
							</div>
						)}

						{/* OS Session */}
						{values.configuration.os?.id && !osPriceLoading && (
							<span className="whitespace-nowrap text-sm text-ipxon-white">{values.configuration.os?.name}</span>
						)}

						{/* Additional IPs */}
						{values.configuration.additionalIp?.name && (
							<span className="whitespace-nowrap text-sm text-ipxon-white">{values.configuration.additionalIp?.name || "No"}</span>
						)}
					</div>
				</div>

				<div className="flex flex-col gap-2">
					{/* Hostname */}
					{values.configuration?.serverName && (
						<span className="text-right">-</span>
					)}

					{/* BGP Session */}
					{values.configuration.bgp?.id && (
						<div className="flex gap-2.5 justify-end text-sm whitespace-nowrap">
							{bgpPriceLoading && <BeatLoader color="#d1d5db" size={6} />}
							{values.configuration.bgp?.id && !bgpPriceLoading && (
								<span className="uppercase">{bgpPrice ? bgpPrice : "Price unavailable"}</span>
							)}
						</div>
					)}

					{/* OS Session */}
					{values.configuration.os?.id && !osPriceLoading && (
						<div className="flex gap-2.5 justify-end text-sm whitespace-nowrap">
							{osPriceLoading && <BeatLoader color="#d1d5db" size={6} />}
							<span className="uppercase">{osPrice ? osPrice : "Price unavailable"}</span>
						</div>
					)}

					{/* Additional IPs */}
					{values.configuration.additionalIp?.name && (
						<div className="flex gap-2.5 justify-end text-sm whitespace-nowrap">
							{additionalIpPriceLoading && <BeatLoader color="#d1d5db" size={6} />}
							{values.configuration.additionalIp?.id && !additionalIpPriceLoading && (
								<span className="uppercase">
									{additionalIpPrice ? additionalIpPrice : "Price unavailable"}
								</span>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const TooltipIconSvg = () => (
	<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M7.5 0.302734C3.36378 0.302734 0 3.66652 0 7.80273C0 11.939 3.36378 15.3027 7.5 15.3027C11.6362 15.3027 15 11.939 15 7.80273C15 3.66652 11.6362 0.302734 7.5 0.302734ZM7.5 1.30273C11.0958 1.30273 14 4.20697 14 7.80273C14 11.3985 11.0958 14.3027 7.5 14.3027C3.90423 14.3027 1 11.3985 1 7.80273C1 4.20697 3.90423 1.30273 7.5 1.30273ZM7.5 3.63607C6.12522 3.63607 5 4.76128 5 6.13607C4.99627 6.40022 5.23582 6.64648 5.5 6.64648C5.76418 6.64648 6.00373 6.40022 6 6.13607C6 5.30172 6.66565 4.63607 7.5 4.63607C8.33435 4.63607 9 5.30172 9 6.13607C9.00595 6.82075 8.65328 7.17112 8.16147 7.63607C7.91555 7.86853 7.6425 8.11037 7.41147 8.42773C7.18043 8.7451 7 9.15753 7 9.63607C6.99627 9.90022 7.23582 10.1465 7.5 10.1465C7.76418 10.1465 8.00373 9.90022 8 9.63607C8 9.38898 8.0724 9.21732 8.21875 9.01627C8.3651 8.81524 8.59297 8.602 8.84895 8.36002C9.36003 7.87687 10.0049 7.3574 9.99998 6.13605C9.99998 4.77072 8.87303 3.63607 7.5 3.63607ZM7.5 10.6361C7.13182 10.6361 6.83333 10.9345 6.83333 11.3027C6.83333 11.6709 7.13182 11.9694 7.5 11.9694C7.86818 11.9694 8.16667 11.6709 8.16667 11.3027C8.16667 10.9346 7.86818 10.6361 7.5 10.6361Z"
			fill="#FAFAFA"
		/>
	</svg>
);
